"use client";
import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import axios, { AxiosResponse } from 'axios';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Definindo interfaces
interface FormData {
  ipServidor: string;
  cpf: string;
  numero: string;
  tipo: 'protocolo' | 'certidao';
}

interface TipoConsulta {
  label: string;
  value: 'protocolo' | 'certidao';
}

interface ResultadoData {
  apresentante?: string;
  dataEntrada?: string;
  exigencia?: string;
  naturezaDocumento?: string;
  numeroDocumento?: string;
  observacoes?: string;
  statusAtual?: string;
  error?: string;
}

interface Resultado {
  tipo: 'protocolo' | 'certidao';
  data: ResultadoData;
  timestamp: Date;
}

const ConsultaAPI: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    ipServidor: '192.168.0.141:8443',
    cpf: '',
    numero: '',
    tipo: 'protocolo',
  });

  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const tiposConsulta: TipoConsulta[] = [
    { label: 'Protocolo', value: 'protocolo' },
    { label: 'Certidão', value: 'certidao' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatarCPF = (cpf: string): string => {
    cpf = cpf.replace(/\D/g, '');
    if (cpf.length <= 11) {
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedCPF = formatarCPF(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpf: formattedCPF,
    }));
  };

  const realizarConsulta = async () => {
    setLoading(true);
    setError(null);
    setResultado(null);

    try {
      // Validar campos
      if (!formData.cpf || !formData.numero) {
        throw new Error('CPF e número são obrigatórios');
      }

      // Remover formatação do CPF
      const cpfLimpo = formData.cpf.replace(/\D/g, '');

      // Construir URL baseada no tipo
      const url = `http://187.63.75.98:8877/consultasite/consultar/${formData.tipo}/${cpfLimpo}/${formData.numero}`;

      const response: AxiosResponse<ResultadoData> = await axios.get(url);
      const responseData = response.data;

      if (responseData.error) {
        setError(responseData.error);
        return;
      }

      // Criar objeto resultado com todos os campos
      setResultado({
        tipo: formData.tipo,
        data: responseData,
        timestamp: new Date(),
      });

    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro na consulta');
    } finally {
      setLoading(false);
    }
  };

  const renderizarDados = () => {
    if (!resultado) return null;

    const dados = resultado.data?.data;
    console.log(" vejamos", dados)

    return (
      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="field">
            <label className="font-bold text-900">Apresentante</label>
            <div className="p-inputtext">{dados.apresentante || 'N/A'}</div>
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="font-bold text-900">Data de Entrada</label>
            <div className="p-inputtext">{dados.dataEntrada || 'N/A'}</div>
          </div>
        </div>

        <div className="col-12">
          <div className="field">
            <label className="font-bold text-900 mb-2 block">Exigência</label>
            <Card className="shadow-1">
              <div
                dangerouslySetInnerHTML={{ __html: dados.exigencia || 'Nenhuma exigência' }}
                className="exigencia-content"
                style={{
                  fontFamily: 'Tahoma, sans-serif',
                  lineHeight: '1.6',
                  fontSize: '14px',
                  minHeight: '100px',
                }}
              />
            </Card>
          </div>
        </div>

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="font-bold text-900">Natureza do Documento</label>
            <div className="p-inputtext">{dados.naturezaDocumento || 'N/A'}</div>
          </div>
        </div>

        {resultado.tipo === 'protocolo' && dados.numeroDocumento && (
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-bold text-900">Número do Documento</label>
              <div className="p-inputtext">{dados.numeroDocumento}</div>
            </div>
          </div>
        )}

        <div className="col-12 md:col-6">
          <div className="field">
            <label className="font-bold text-900">Observações</label>
            <div className="p-inputtext">{dados.observacoes || 'N/A'}</div>
          </div>
        </div>

        <div className="col-12">
          <div className="field">
            <label className="font-bold text-900">Status Atual</label>
            <div className="p-inputtext">{dados.statusAtual || 'N/A'}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="grid justify-content-center">
        <div className="col-12 lg:col-8">
          <Card title="Consulta de Protocolos e Certidões" className="shadow-2">
            {/* Formulário de Consulta */}
            <div className="grid formgrid p-fluid">
              <div className="col-12 md:col-4">
                <div className="field">
                  <label htmlFor="cpf" className="font-bold block mb-2">
                    CPF <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    id="cpf"
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleCPFChange}
                    placeholder="000.000.000-00"
                    className="w-full"
                    maxLength={14}
                  />
                </div>
              </div>

              <div className="col-12 md:col-4">
                <div className="field">
                  <label htmlFor="numero" className="font-bold block mb-2">
                    Número <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    id="numero"
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    placeholder="Número do protocolo/certidão"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="col-12 md:col-4">
                <div className="field">
                  <label htmlFor="tipo" className="font-bold block mb-2">
                    Tipo de Consulta
                  </label>
                  <Dropdown
                    id="tipo"
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleInputChange}
                    options={tiposConsulta}
                    optionLabel="label"
                    placeholder="Selecione o tipo"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="col-12">
                <Button
                  label="Realizar Consulta"
                  icon="pi pi-search"
                  onClick={realizarConsulta}
                  loading={loading}
                  className="w-full"
                  disabled={!formData.cpf || !formData.numero}
                />
              </div>
            </div>

            <Divider />

            {/* Resultados */}
            {loading && (
              <div className="text-center py-6">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                <p className="mt-3 text-600">Realizando consulta...</p>
              </div>
            )}

            {error && (
              <Message
                severity="error"
                text={error}
                className="w-full mb-4"
              />
            )}

            {resultado && (
              <Card title={`Resultado da Consulta - ${resultado.tipo === 'protocolo' ? 'Protocolo' : 'Certidão'}`} className="mt-4">
                <div className="flex align-items-center mb-4">
                  <i className="pi pi-calendar mr-2 text-600"></i>
                  <span className="text-600">
                    Consulta realizada em: {resultado.timestamp && resultado.timestamp.toLocaleString('pt-BR')}
                  </span>
                </div>

                {renderizarDados()}

                <div className="mt-4">
                  <Button
                    label="Nova Consulta"
                    icon="pi pi-refresh"
                    onClick={() => {
                      setResultado(null);
                      setError(null);
                    }}
                    className="p-button-outlined"
                  />
                </div>
              </Card>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ConsultaAPI;
